import React, { SyntheticEvent, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContentProps } from 'react-toastify';

import {
  InstantNotificationStreamModel,
  BackofficeInstantNotificationType,
} from 'api/backofficeNotificationApiClient';
import {
  approvedEntitiesNotificationIdsSelector,
  isApprovedEntityInstantNotificationReadSelector,
} from 'redux/selectors/notificationSelectors';
import BaseAlert from 'components/wrappers/BaseAlert';
import BaseToast from 'components/wrappers/BaseToast';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import CongratulationsModal from 'components/modals/CongratulationsModal';
import { removeApprovedEntitiesNotifications } from 'redux/reducers/notificationSlice';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';

import styles from './EntityApprovedInstantNotification.module.less';

type OwnProps = {
  toastId: string;
  approvedEntity: InstantNotificationStreamModel;
};

// ToastContentProps are embeded by toast when using custom component
type Props = OwnProps & Partial<ToastContentProps>;

export default function EntityApprovedInstantNotification(props: Props) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { approvedEntity, toastId } = props;
  const { type, notificationId, entityKey } = approvedEntity;
  const approvedEntitiesNotificationIds = useAppSelector(
    approvedEntitiesNotificationIdsSelector,
  );
  const isApprovedEntityInstantNotificationRead = useAppSelector(
    isApprovedEntityInstantNotificationReadSelector(notificationId),
  );

  const closeApprovedPopup = () => {
    BaseToast.dismiss(toastId);
  };

  const handleClose = async () => {
    closeApprovedPopup();

    // Do not call API if notification is already read
    if (isApprovedEntityInstantNotificationRead) return;

    const result = await dispatch(
      removeApprovedEntitiesNotifications([notificationId]),
    );

    if (!removeApprovedEntitiesNotifications.fulfilled.match(result)) {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const markAllNotificationsAsRead = useCallback(async () => {
    // Do not call API if all notifications are already read
    if (!approvedEntitiesNotificationIds.length) return;

    const result = await dispatch(
      removeApprovedEntitiesNotifications(approvedEntitiesNotificationIds),
    );

    if (!removeApprovedEntitiesNotifications.fulfilled.match(result)) {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  }, [approvedEntitiesNotificationIds, dispatch]);

  const handleKeyClick = async (event: SyntheticEvent) => {
    event.preventDefault();

    closeApprovedPopup();
    markAllNotificationsAsRead();

    switch (type) {
      case BackofficeInstantNotificationType.ApprovedCustomerInstantNotify:
        return navigate(`/customer/${entityKey}`);

      case BackofficeInstantNotificationType.ApprovedAssignmentInstantNotify:
        return navigate(`/assignment/${entityKey}`);

      default:
        return;
    }
  };

  const getEntityLinkName = () => {
    switch (type) {
      case BackofficeInstantNotificationType.ApprovedCustomerInstantNotify:
        return 'customer';

      case BackofficeInstantNotificationType.ApprovedAssignmentInstantNotify:
        return 'assignment';

      default:
        return '';
    }
  };

  return (
    <CongratulationsModal
      onClose={handleClose}
      content={
        <div className={styles.popupText}>
          Your {getEntityLinkName()}{' '}
          <a href="/" className={styles.entityKey} onClick={handleKeyClick}>
            {entityKey}
          </a>{' '}
          was approved.
        </div>
      }
    />
  );
}
